import { Controller } from "@hotwired/stimulus"
import Chart from 'chart.js/auto'

export default class extends Controller {
  static values = {
    type: String,      // pie, donut, bar
    data: Object,      // {labels: [], values: [], colors: []}
    title: String
  }

  connect() {
    this.initializeChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  initializeChart() {
    const canvas = this.element.querySelector('canvas')
    if (!canvas) return

    const ctx = canvas.getContext('2d')

    // Prepare chart configuration
    const config = this.getChartConfig()

    // Create chart
    this.chart = new Chart(ctx, config)
  }

  getChartConfig() {
    const chartType = this.typeValue === 'donut' ? 'doughnut' : this.typeValue

    // Line and Bar charts always use raw values (not percentages)
    if (['line', 'bar'].includes(this.typeValue)) {
      return this.getTimeSeriesConfig(chartType)
    }

    // Distribution charts (pie/donut) use percentages
    return this.getDistributionConfig(chartType)
  }

  getDistributionConfig(chartType) {
    // Calculate percentages from raw values
    const rawValues = this.dataValue.values || []
    const total = rawValues.reduce((sum, val) => sum + parseFloat(val), 0)
    const percentages = rawValues.map(val => total > 0 ? (parseFloat(val) / total * 100) : 0)

    return {
      type: chartType,
      data: {
        labels: this.dataValue.labels || [],
        datasets: [{
          label: '', // Pas de label pour le dataset pour éviter "undefined" dans la légende
          data: percentages,
          backgroundColor: this.dataValue.colors || this.getDefaultColors(),
          borderWidth: 2,
          borderColor: '#ffffff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: chartType === 'bar' ? false : true,
        plugins: {
          legend: {
            display: chartType !== 'bar', // Cacher la légende pour les graphiques bar
            position: 'bottom',
            labels: {
              font: {
                family: 'DM Sans',
                size: 14
              },
              color: '#0B1D39',
              padding: 15,
              usePointStyle: true,
              pointStyle: 'circle'
            }
          },
          tooltip: {
            backgroundColor: 'rgba(11, 29, 57, 0.9)',
            titleFont: {
              family: 'Syne',
              size: 14,
              weight: 'bold'
            },
            bodyFont: {
              family: 'DM Sans',
              size: 13
            },
            padding: 12,
            cornerRadius: 8,
            displayColors: true,
            callbacks: {
              label: (context) => {
                const label = context.label || ''
                const rawValue = rawValues[context.dataIndex] || 0

                // Pour les graphiques bar, context.parsed est un objet {x, y}
                const percentage = typeof context.parsed === 'object' ? context.parsed.y : context.parsed

                if (typeof percentage === 'number') {
                  return ` ${label}: ${percentage.toFixed(1)}% (${rawValue})`
                }
                return ` ${label}: ${rawValue}`
              }
            }
          },
          title: this.titleValue ? {
            display: true,
            text: this.titleValue,
            font: {
              family: 'Syne',
              size: 18,
              weight: 'bold'
            },
            color: '#0B1D39',
            padding: {
              top: 10,
              bottom: 20
            }
          } : {
            display: false
          }
        },
        // Specific options for donut charts
        ...(this.typeValue === 'donut' && {
          cutout: '60%'
        })
      }
    }
  }

  getTimeSeriesConfig(chartType) {
    const options = this.dataValue.options || {}
    const values = this.dataValue.values || []
    const color = options.color || '#0B1D39'

    // Parse min/max values - convert to number or undefined
    const minValue = options.min !== undefined && options.min !== '' ? parseFloat(options.min) : undefined
    const maxValue = options.max !== undefined && options.max !== '' ? parseFloat(options.max) : undefined

    // Convert hex to rgba for background (10% opacity)
    const backgroundColor = chartType === 'line'
      ? this.hexToRgba(color, 0.1)
      : color

    return {
      type: chartType,
      data: {
        labels: this.dataValue.labels || [],
        datasets: [{
          data: values,
          borderColor: color,
          backgroundColor: backgroundColor,
          borderWidth: chartType === 'line' ? 3 : 0,
          fill: chartType === 'line',
          tension: chartType === 'line' ? 0.4 : 0,
          cubicInterpolationMode: chartType === 'line' ? 'monotone' : undefined,
          pointRadius: chartType === 'line' ? 5 : 0,
          pointHoverRadius: chartType === 'line' ? 7 : 0,
          pointBackgroundColor: color,
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          borderRadius: chartType === 'bar' ? 8 : 0,
          borderSkipped: chartType === 'bar' ? false : undefined
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: options.horizontal ? 'y' : 'x',
        scales: {
          y: {
            beginAtZero: false,
            min: minValue,
            max: maxValue,
            ticks: {
              font: { family: 'DM Sans', size: 12 },
              color: '#757575',
              callback: (value) => {
                const suffix = options.suffix ? ` ${options.suffix}` : ''
                if (options.format === 'number') return typeof value === 'number' ? value + suffix : value
                return typeof value === 'number' ? value.toFixed(2) + ' €' : value
              }
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.05)',
              drawBorder: false
            }
          },
          x: {
            ticks: {
              font: { family: 'DM Sans', size: 12 },
              color: '#757575'
            },
            grid: { display: false }
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(11, 29, 57, 0.95)',
            titleFont: { family: 'Syne', size: 14, weight: 'bold' },
            bodyFont: { family: 'DM Sans', size: 13 },
            padding: 12,
            cornerRadius: 8,
            callbacks: {
              label: (context) => {
                const value = context.parsed.y !== undefined ? context.parsed.y : context.parsed.x
                if (value === null) return ' N/A'
                if (options.format === 'number') return options.suffix ? ` ${value} ${options.suffix}` : ` ${value}`
                return ` ${value.toFixed(2)} €`
              }
            }
          },
          title: this.titleValue ? {
            display: true,
            text: this.titleValue,
            font: { family: 'Syne', size: 18, weight: 'bold' },
            color: '#0B1D39',
            padding: { top: 10, bottom: 20 }
          } : { display: false }
        }
      }
    }
  }


  hexToRgba(hex, alpha) {
    // Remove # if present
    hex = hex.replace('#', '')

    // Parse hex values
    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)

    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  getDefaultColors() {
    // RendementSCPI brand colors (updated palette)
    return [
      '#FCDE1E',  // Jaune vif
      '#2E7D32',  // Vert foncé
      '#66BB6A',  // Vert clair
      '#A7D3F3',  // Bleu clair
      '#0B1D39',  // Bleu nuit
      '#FAF7F2',  // Crème/Fond
      '#111111',  // Noir profond
      '#C8E6C9',  // Vert très pâle
      '#E3F2FD',  // Bleu très pâle
      '#757575'   // Gris neutre
    ]
  }
}
